'use client';

import { Dialog, DialogClose, DialogContent, DialogHeader } from '@shared/components/shadcn';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { useEducationalTutorial } from '../../hooks/useEducationalTutorial';
import { EducationalTutorialCloseButton } from '../CloseButton/EducationalTutorialCloseButton';
import { EducationalTutorialContent } from '../EducationalTutorialContent/EducationalTutorialContent';
import { EducationalTutorialImage } from '../Image/EducationalTutorialImage';

type EducationalModalProps = ComponentProps<'div'> & {
  onComplete?: () => void;
};

function EducationalModal({ onComplete, ...props }: EducationalModalProps) {
  const t = useTranslations('global_dialogs.educational');
  const tutorial = useEducationalTutorial({ onComplete });

  return (
    <Dialog open={tutorial.isOpen} onOpenChange={tutorial.closeDialog}>
      <DialogContent
        variant="dialog"
        className="gap-6 overflow-hidden bg-background"
        showCloseButton={false}
        onKeyDown={tutorial.handleKeyDown}
        onPointerDownOutside={event => event.preventDefault()}
        onInteractOutside={event => event.preventDefault()}
      >
        <div
          key={`educational-guide-${tutorial.stepKey}`}
          data-track-container="Guide"
          data-track-feature="Education"
          data-track-step={tutorial.currentStep}
          data-track-title={t(`${tutorial.stepKey}.title`)}
        >
          <DialogHeader className="h-40">
            <DialogClose asChild>
              <EducationalTutorialCloseButton tutorial={tutorial} />
            </DialogClose>
            <EducationalTutorialImage stepKey={tutorial.stepKey} className="absolute inset-x-0 top-0" />
          </DialogHeader>
          <EducationalTutorialContent tutorial={tutorial} variant="modal" className="pt-6" {...props} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { EducationalModal };
